import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Sidebar, Menu, MenuItem, SubMenu, menuClasses } from 'react-pro-sidebar';
import { Typography, useTheme } from '@mui/material';
import { Icon } from '../icons';
import { Menu as MenuIcon } from '@mui/icons-material';
import { hexToRGBA } from '../../utils/hex-to-rgba';

import { useAuth } from "../../hooks/useAuth";
import { useTranslation } from "react-i18next";

import './SidebarStyles.css'

const GroupSidebar = ({ collapsed, setCollapsed }) => {
  const { t } = useTranslation();
  const user = useSelector((state) => state.auth.user);
  const isRoot = user?.is_root;
  const isAdmin = user?.is_admin;
  
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { group_code, project_code } = useParams();
  const [hasImage, setHasImage] = useState(false);
  const [toggled, setToggled] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  
  const auth = useAuth();

  const isMachinePageActive = location.pathname.startsWith('/master/machine');
  const isItemPageActive = location.pathname.startsWith('/master/item');
  const isWorkerPageActive = location.pathname.startsWith('/master/worker');

  const handleMenuClick = (path) => {
    navigate(path);
  };

  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    auth.logout();
    navigate('/login');
  };

  const sidebarTheme = theme.palette.mode === 'light' ? {
    sidebar: {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
    },
    menu: {
      menuContent: theme.palette.background.default,
      icon: theme.palette.primary.main,
      hover: {
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.text.primary,
      },
      disabled: {
        color: theme.palette.action.disabled,
      },
    },
  } : {
    sidebar: {
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary,
    },
    menu: {
      menuContent: theme.palette.background.paper,
      icon: theme.palette.primary.light,
      hover: {
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.text.primary,
      },
      disabled: {
        color: theme.palette.action.disabled,
      },
    },
  };

  const menuItemStyles = {
    root: {
      fontSize: '13px',
      fontWeight: 400,
    },
    icon: {
      color: sidebarTheme.menu.icon,
      '& svg': {
        width: '20px',
        height: '20px',
      },
      [`&.${menuClasses.disabled}`]: {
        color: sidebarTheme.menu.disabled.color,
      },
    },
    SubMenuExpandIcon: {
      color: '#b6b7b9',
    },
    // subMenuContent: ({ level }) => ({
    //   backgroundColor:
    //     level === 0
    //       ? hexToRGBA(sidebarTheme.menu.menuContent, hasImage && !collapsed ? 0.4 : 1)
    //       : 'transparent',
    // }),
    button: {
      [`&.${menuClasses.disabled}`]: {
        color: sidebarTheme.menu.disabled.color,
      },
      '&:hover': {
        backgroundColor: hexToRGBA(sidebarTheme.menu.hover.backgroundColor, hasImage ? 0.8 : 1),
        color: sidebarTheme.menu.hover.color,
      },
    },
    label: ({ open }) => ({
      fontWeight: open ? 600 : undefined,
    }),
  };

  if (!user) {
    return null;
  }

  return (
    <Sidebar
      collapsed={collapsed}
      toggled={toggled}
      onBackdropClick={() => setToggled(false)}
      backgroundColor={hexToRGBA(sidebarTheme.sidebar.backgroundColor, hasImage ? 0.9 : 1)}
      rootStyles={{
        height: '100%',
        color: sidebarTheme.sidebar.color,
        width: '240px',
        minWidth: '240px',
        borderRight: `1px solid ${theme.palette.divider}`,
        borderLeft: 'none',
        borderBlock: 'none'
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {/* 기존 "관리 메뉴" */}
          <div style={{ marginBottom: '15px' }}>
            <div style={{ marginBlock: '8px', marginInline: '30px', display: 'flex', alignItems: 'center' }}>
              <Typography
                fontWeight={600}
                style={{ fontSize: '14px', opacity: 0.7, letterSpacing: '0.5px' }}
              >
                {t('sidebars.master.title')}
              </Typography>
            </div>
            <Menu menuItemStyles={menuItemStyles}>
              <MenuItem
                onClick={() => handleMenuClick(`/master/machine/${group_code}/${project_code}`)}
                disabled={isMachinePageActive}
                style={{
                  height: 'auto',
                  borderRadius: '6px',
                  marginBlock: '2px',
                  marginInline: '10px',
                  paddingBlock: '10px',
                  backgroundColor: isMachinePageActive ? theme.palette.primary.lightOpacity : 'transparent',
                }}
              >
                <Icon 
                  name="squareSettingsIcon" 
                  style={{ 
                    width: '20px', 
                    height: '20px',
                    color: isMachinePageActive ? theme.palette.primary.main : 'rgba(124, 124, 124, 1)'
                  }} 
                />
                <Typography 
                  variant="caption" 
                  style={{ 
                    fontSize: '16px', 
                    fontWeight: 500, 
                    opacity: 0.9,
                    color: isMachinePageActive ? theme.palette.primary.main : 'rgba(124, 124, 124, 1)'
                  }}>
                  {t('sidebars.master.machineManagement')}
                </Typography>
              </MenuItem>
              <MenuItem
                onClick={() => handleMenuClick(`/master/item/${group_code}/${project_code}`)}
                disabled={isItemPageActive}
                style={{
                  height: 'auto',
                  borderRadius: '6px',
                  marginBlock: '2px',
                  marginInline: '10px',
                  paddingBlock: '10px',
                  backgroundColor: isItemPageActive ? theme.palette.primary.lightOpacity : 'transparent',
                }}
              >
                <Icon 
                  name="boxIcon" 
                  style={{ 
                    width: '20px', 
                    height: '20px',
                    color: isItemPageActive ? theme.palette.primary.main : 'rgba(124, 124, 124, 1)'
                  }} 
                />
                <Typography 
                  variant="caption" 
                  style={{ 
                    fontSize: '16px', 
                    fontWeight: 500, 
                    opacity: 0.9,
                    color: isItemPageActive ? theme.palette.primary.main : 'rgba(124, 124, 124, 1)'
                  }}>
                  {t('sidebars.master.itemManagement')}
                </Typography>
              </MenuItem>
              <MenuItem
                onClick={() => handleMenuClick(`/master/worker/${group_code}/${project_code}`)}
                disabled={isWorkerPageActive}
                style={{
                  height: 'auto',
                  borderRadius: '6px',
                  marginBlock: '2px',
                  marginInline: '10px',
                  paddingBlock: '10px',
                  backgroundColor: isWorkerPageActive ? theme.palette.primary.lightOpacity : 'transparent',
                }}
              >
                <Icon 
                  name="workerIcon" 
                  style={{ 
                    width: '20px', 
                    height: '20px',
                    color: isWorkerPageActive ? theme.palette.primary.main : 'rgba(124, 124, 124, 1)'
                  }} 
                />
                <Typography 
                  variant="caption" 
                  style={{ 
                    fontSize: '16px', 
                    fontWeight: 500, 
                    opacity: 0.9,
                    color: isWorkerPageActive ? theme.palette.primary.main : 'rgba(124, 124, 124, 1)'
                  }}>
                  {t('sidebars.master.workerManagement')}
                </Typography>
              </MenuItem>
            </Menu>
          </div>
        </div>
      </div>
    </Sidebar>
  );
};

export default GroupSidebar;